/**
 * ChartOfAccountsPanel — manage the ledger accounts used to categorize journal entries.
 * The Category dropdown drives Balance Sheet & P&L grouping.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, BookMarked, Search } from "lucide-react";

const SIDE_TONE = {
  assets: "bg-emerald-100 text-emerald-800 border-emerald-200",
  liabilities: "bg-red-100 text-red-800 border-red-200",
  equity: "bg-blue-100 text-blue-800 border-blue-200",
  income: "bg-amber-100 text-amber-800 border-amber-200",
  expense: "bg-orange-100 text-orange-800 border-orange-200",
};

const emptyForm = { code: "", name: "", category: "", type: "" };

export default function ChartOfAccountsPanel() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [sideFilter, setSideFilter] = useState("all");
  const [editing, setEditing] = useState(null);   // account or null
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [c, cat] = await Promise.all([
        api.get("/accounts/coa"),
        api.get("/accounts/coa/categories"),
      ]);
      setRows(c.data);
      setCategories(cat.data);
    } catch (e) { toast.error("Failed to load Chart of Accounts"); }
  };
  useEffect(() => { load(); }, []);

  const catByKey = useMemo(() => Object.fromEntries(categories.map((c) => [c.key, c])), [categories]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((r) => {
      const side = catByKey[r.category]?.side || "";
      if (sideFilter !== "all" && side !== sideFilter) return false;
      if (!ql) return true;
      return (
        (r.code || "").toLowerCase().includes(ql) ||
        (r.name || "").toLowerCase().includes(ql) ||
        (r.category || "").toLowerCase().includes(ql)
      );
    });
  }, [rows, q, sideFilter, catByKey]);

  const openCreate = () => { setForm(emptyForm); setCreating(true); setEditing(null); };
  const openEdit = (acc) => {
    setEditing(acc);
    setCreating(false);
    setForm({
      code: acc.code || "",
      name: acc.name || "",
      category: acc.category || "",
      type: acc.type || "",
    });
  };
  const close = () => { setCreating(false); setEditing(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/accounts/coa/${encodeURIComponent(editing.code)}`, {
          name: form.name, category: form.category || null,
        });
        toast.success(`Updated ${editing.code}`);
      } else {
        if (!form.code.trim()) return toast.error("Code is required");
        await api.post("/accounts/coa", {
          code: form.code, name: form.name, category: form.category || null,
        });
        toast.success(`Account ${form.code} created`);
      }
      await load();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (acc) => {
    if (!window.confirm(`Delete account ${acc.code} · ${acc.name}?`)) return;
    try {
      await api.delete(`/accounts/coa/${encodeURIComponent(acc.code)}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // Group categories by side for the dropdown
  const groupedCategories = useMemo(() => {
    const g = {};
    categories.forEach((c) => {
      (g[c.side] = g[c.side] || []).push(c);
    });
    return g;
  }, [categories]);

  const sideLabels = { assets: "Assets", liabilities: "Liabilities", equity: "Equity", income: "Income (P&L)", expense: "Expense (P&L)" };

  return (
    <div className="pt-4 space-y-4" data-testid="coa-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-[Outfit] text-2xl font-semibold flex items-center gap-2">
            <BookMarked className="w-6 h-6 text-[#C05621]" /> Chart of Accounts
          </h2>
          <p className="text-xs text-[#8B7355]">Assign a category to each ledger so the Balance Sheet and P&amp;L Statement auto-group correctly.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-[#8B7355] absolute left-2 top-2.5" />
            <Input placeholder="Search code, name, category…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-7 w-64" data-testid="coa-search" />
          </div>
          <Select value={sideFilter} onValueChange={setSideFilter}>
            <SelectTrigger className="w-40" data-testid="coa-side-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All sides</SelectItem>
              <SelectItem value="assets">Assets</SelectItem>
              <SelectItem value="liabilities">Liabilities</SelectItem>
              <SelectItem value="equity">Equity</SelectItem>
              <SelectItem value="income">Income</SelectItem>
              <SelectItem value="expense">Expense</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={openCreate} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="coa-add-btn">
            <Plus className="w-4 h-4 mr-1" /> New Account
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">Code</th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Type</th>
              <th className="text-left px-4 py-2">Category</th>
              <th className="text-left px-4 py-2">Side / Group</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="coa-rows">
            {filtered.map((r) => {
              const cat = catByKey[r.category];
              return (
                <tr key={r.code} className="border-t border-[#EAE5D9] hover:bg-[#FDFBF7]" data-testid={`coa-row-${r.code}`}>
                  <td className="px-4 py-2 font-mono text-xs">{r.code}</td>
                  <td className="px-4 py-2 font-medium">{r.name}{r.is_default && <Badge className="ml-2 bg-[#FEF9EA] text-[#8B7355] border-[#EAE5D9] text-[10px]" variant="outline">seeded</Badge>}</td>
                  <td className="px-4 py-2 uppercase text-xs text-[#8B7355]">{r.type}</td>
                  <td className="px-4 py-2 text-xs">
                    {r.category ? (
                      <Badge className={`${SIDE_TONE[cat?.side] || "bg-slate-100 text-slate-700"} border`} variant="outline">
                        {cat?.label || r.category}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-red-600 border-red-200 bg-red-50">uncategorized</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-[#5C4033]">
                    {cat ? <span className="capitalize">{cat.side} · {cat.group}</span> : <span className="text-[#8B7355] italic">—</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => openEdit(r)} className="text-[#C05621] hover:bg-[#FEF9EA] rounded p-1 mr-1" data-testid={`coa-edit-${r.code}`}><Pencil className="w-4 h-4" /></button>
                    {!r.is_default && (
                      <button onClick={() => remove(r)} className="text-red-600 hover:bg-red-50 rounded p-1" data-testid={`coa-del-${r.code}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-[#8B7355]">No accounts match</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={creating || !!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-[Outfit]">
              {editing ? `Edit Account ${editing.code}` : "New Ledger Account"}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#8B7355]">
              Assign a category to drive Balance Sheet grouping and P&amp;L bucket. Default accounts cannot be renumbered.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Code {!editing && <span className="text-red-600">*</span>}</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. 5010" disabled={!!editing} data-testid="coa-form-code" />
              {!editing && <p className="text-[10px] text-[#8B7355] mt-1">Tip: 1xxx = Asset, 2xxx = Liability, 3xxx = Equity, 4xxx = Income, 5xxx = Expense.</p>}
            </div>
            <div>
              <Label>Account Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Rent Expense" data-testid="coa-form-name" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="coa-form-category"><SelectValue placeholder="Pick a bucket" /></SelectTrigger>
                <SelectContent className="bg-white max-h-80">
                  {Object.entries(groupedCategories).map(([side, list]) => (
                    <React.Fragment key={side}>
                      <div className="px-2 py-1 text-[10px] uppercase font-semibold text-[#8B7355] tracking-wider">{sideLabels[side] || side}</div>
                      {list.map((c) => (
                        <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#8B7355] mt-1">Drives Balance Sheet grouping (Fixed Assets, Current Liabilities…) or P&amp;L bucket (Direct/Indirect Expenses).</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="coa-form-save">
              {saving ? "Saving…" : (editing ? "Save" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
